/**
 * Brand directory — the primary Brands & Vendors view.
 *
 * Answers "for every brand we carry, how far has it got?" through four readiness
 * signals (logo, products, ingestion, claim) rolled up into a lifecycle stage.
 * Filtering and counting are recomputed client-side from the rows so an
 * optimistic write anywhere in the area moves the pills and chips immediately.
 */

import React, { useMemo, useState } from 'react';
import { Box, ButtonBase, Alert, Dialog, DialogActions, DialogContent, DialogTitle, TextField, useMediaQuery, useTheme } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { brandDirectoryApi, BrandDirectoryRow } from '../../services/brandDirectoryApi';
import { brandManagementApi } from '../../services/brandManagementApi';
import {
  bvCardSx,
  bvColor,
  bvContentSx,
  bvPageSx,
  bvRadius,
  bvTransition,
  bvType,
  tabular,
} from './brandVendorsTokens';
import {
  companyFactsLine,
  ingestionDotColor,
  lifecycleOf,
  matchesQuery,
  matchesStageFilter,
  productsLabel,
  productsScopeLabel,
  productsScopeTitle,
  readinessChipsOf,
  SPEC_MAPPING_LABEL,
  SPEC_MAPPING_NOTE,
  StageFilter,
  stageCountsOf,
} from './brandLifecycle';
import { bvRoutes, chipTarget } from './brandVendorsRoutes';
import {
  BvButton,
  BvCard,
  BvSearchInput,
  BvEmptyState,
  IconArrowOut,
  IconImage,
  IconPlus,
  LogoFrame,
  ReadinessChipRow,
  ReadinessLegend,
  BvPageHeader,
  StagePill,
  StatusDot,
} from './components/BrandVendorsPrimitives';
import BrandPeekDrawer from './components/BrandPeekDrawer';
import { dealerVerifierSummary, useDealerVerifiers } from './useDealerVerifiers';
import { BrandDirectoryListSkeleton, StageCountValueSkeleton } from './components/BrandVendorsSkeletons';

/** Shared grid template so the header strip and every row stay in lockstep. */
const GRID = 'minmax(112px,1.7fr) 132px minmax(80px,0.8fr) minmax(74px,0.7fr) minmax(96px,1fr) minmax(96px,1.2fr) 28px';

const STAGE_PILLS: Array<{ key: Exclude<StageFilter, 'all' | 'unclaimed'>; label: string; note: string; skeletonWidth: number }> = [
  { key: 'registered', label: 'Registered', note: 'Record exists, no ingestion.', skeletonWidth: 36 },
  { key: 'ingesting', label: 'Ingesting', note: 'Products flowing, unclaimed.', skeletonWidth: 32 },
  { key: 'claimed', label: 'Claimed', note: 'Rep on board, gaps remain.', skeletonWidth: 24 },
  { key: 'complete', label: 'Complete', note: 'Logo, products, ingestion, rep.', skeletonWidth: 20 },
];

export const BrandVendorsDirectoryPage: React.FC = () => {
  const theme = useTheme();
  const fullScreenDialog = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState('');
  const [stage, setStage] = useState<StageFilter>('all');
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState('');

  const peekId = searchParams.get('brand');

  const { data, isLoading, error } = useQuery({
    queryKey: ['brand-directory'],
    queryFn: () => brandDirectoryApi.list(),
  });

  const { lookup: dealerLookup } = useDealerVerifiers();

  const brands = useMemo(() => data?.brands || [], [data]);
  const unregisteredBrands = useMemo(() => data?.unregisteredBrands || [], [data]);
  // Recount locally rather than trusting the server payload, so the pills agree
  // with the rows after an optimistic write.
  const counts = useMemo(() => stageCountsOf(brands), [brands]);

  const rows = useMemo(
    () => brands.filter((row) => matchesQuery(row, query) && matchesStageFilter(row, stage)),
    [brands, query, stage]
  );

  const createMutation = useMutation({
    mutationFn: (name: string) => brandManagementApi.createBrand({ name: name.trim() }),
    onSuccess: (brand) => {
      setCreateOpen(false);
      setNewName('');
      queryClient.invalidateQueries({ queryKey: ['brand-directory'] });
      navigate(bvRoutes.brand(brand.id));
    },
  });

  const filterActive = stage !== 'all' || query.trim().length > 0;
  const setPeek = (brandId: string | null) =>
    setSearchParams(brandId ? { brand: brandId } : {}, { replace: true });

  return (
    <Box sx={bvPageSx}>
      <Box sx={{ ...bvContentSx, display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <BvPageHeader
          title="Brands & vendors"
          description="Every brand we carry or want to carry, and how far each one has got — logo on file, products in the catalog, ingestion running, and a rep who has claimed it."
          actions={
            <>
              <BvButton
                size="lg"
                startIcon={<IconImage size={14} />}
                onClick={() => navigate(bvRoutes.logos())}
              >
                Logo library
                {isLoading ? (
                  <StageCountValueSkeleton width={56} height={12} />
                ) : (
                  counts.missingLogos > 0 && (
                    <Box component="span" sx={{ color: bvColor.muted, ml: '5px', ...tabular }}>
                      {counts.missingLogos} missing
                    </Box>
                  )
                )}
              </BvButton>
              <BvButton
                size="lg"
                tone="primary"
                startIcon={<IconPlus size={14} />}
                onClick={() => {
                  createMutation.reset();
                  setNewName('');
                  setCreateOpen(true);
                }}
              >
                Add brand
              </BvButton>
            </>
          }
        />

        {/* stage filter pills */}
        <Box
          sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: '10px' }}
          aria-busy={isLoading || undefined}
        >
          {STAGE_PILLS.map((pill) => {
            const on = stage === pill.key;
            return (
              <ButtonBase
                key={pill.key}
                aria-pressed={on}
                // Clicking the active pill clears back to All.
                onClick={() => {
                  setStage(on ? 'all' : pill.key);
                  setPeek(null);
                }}
                sx={{
                  display: 'block',
                  textAlign: 'left',
                  px: '13px',
                  py: '11px',
                  borderRadius: `${bvRadius.panel}px`,
                  bgcolor: on ? bvColor.surfaceTint : bvColor.surface,
                  border: `1px solid ${on ? bvColor.accent : bvColor.border}`,
                  transition: bvTransition,
                  '&:hover': { borderColor: on ? bvColor.accent : bvColor.accentTint },
                  '&:focus-visible': { outline: `2px solid ${bvColor.accent}`, outlineOffset: 1 },
                }}
              >
                <Box sx={{ fontSize: 20, fontWeight: 700, color: bvColor.ink, lineHeight: 1.1, minHeight: 22, ...tabular }}>
                  {isLoading ? <StageCountValueSkeleton width={pill.skeletonWidth} /> : counts[pill.key]}
                </Box>
                <Box sx={{ ...bvType.buttonSmall, color: on ? bvColor.accentHover : bvColor.ink2, mt: '3px' }}>
                  {pill.label}
                </Box>
                <Box sx={{ ...bvType.caption, color: bvColor.muted, mt: '2px', minHeight: 28, lineHeight: 1.35 }}>
                  {pill.note}
                </Box>
              </ButtonBase>
            );
          })}
        </Box>

        {error && <Alert severity="error">{(error as Error).message}</Alert>}

        <Box sx={bvCardSx}>
          {/* toolbar */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '9px',
              px: '16px',
              py: '11px',
              bgcolor: bvColor.page,
              borderBottom: `1px solid ${bvColor.border}`,
            }}
          >
            <BvSearchInput
              ariaLabel="Search brands"
              value={query}
              onChange={setQuery}
              placeholder="Search brand or category"
            />
            <ButtonBase
              aria-pressed={stage === 'unclaimed'}
              onClick={() => {
                setStage(stage === 'unclaimed' ? 'all' : 'unclaimed');
                setPeek(null);
              }}
              title="Show only brands that no vendor account has claimed. This overlaps the stages above, so it is a filter, not a fifth stage."
              sx={{
                height: 28,
                px: '11px',
                borderRadius: `${bvRadius.pill}px`,
                ...bvType.buttonSmall,
                color: stage === 'unclaimed' ? bvColor.accentHover : bvColor.ink3,
                bgcolor: stage === 'unclaimed' ? bvColor.surfaceTint : bvColor.surface,
                border: `1px solid ${stage === 'unclaimed' ? bvColor.accent : bvColor.border}`,
                transition: bvTransition,
                '&:hover': { borderColor: bvColor.accent },
                '&:focus-visible': { outline: `2px solid ${bvColor.accent}`, outlineOffset: 1 },
              }}
            >
              Unclaimed only
              <Box component="span" sx={{ ml: '6px', color: bvColor.muted, ...tabular }}>
                {isLoading ? '–' : counts.unclaimed}
              </Box>
            </ButtonBase>
            {filterActive && (
              <BvButton
                size="sm"
                tone="danger"
                onClick={() => {
                  setQuery('');
                  setStage('all');
                }}
              >
                Clear
              </BvButton>
            )}
            <Box sx={{ flex: 1 }} />
            <Box sx={{ ...bvType.caption, color: bvColor.muted, ...tabular }}>
              {isLoading ? <StageCountValueSkeleton width={88} height={12} /> : `${rows.length} of ${brands.length} brands`}
            </Box>
          </Box>

          <ReadinessLegend />

          {/* header strip */}
          <Box
            sx={{
              display: { xs: 'none', lg: 'grid' },
              gridTemplateColumns: { xs: '1fr', lg: GRID },
              columnGap: '12px',
              px: '16px',
              py: '9px',
              bgcolor: bvColor.page,
              borderBottom: `1px solid ${bvColor.border}`,
              ...bvType.caption,
              color: bvColor.muted,
            }}
          >
            <Box>Brand</Box>
            <Box sx={{ pr: '36px' }} title="Logo, Products, Ingestion, Claim — see the legend above">Readiness</Box>
            <Box>Stage</Box>
            <Box title="Active, archived and discontinued products; the active count is the one the Product Browser shows. Placeholders and drafts are excluded.">Products</Box>
            <Box title="How a store's authorization for this brand is checked (as on Dealer lists), and the spec schema mapping its catalog is normalized with">
              Dealer check · spec schema
            </Box>
            <Box>Claimed by · ingestion</Box>
            <Box />
          </Box>

          {isLoading ? (
            <BrandDirectoryListSkeleton grid={GRID} />
          ) : rows.length === 0 ? (
            <BvEmptyState headline="No brands in this view." py="56px" />
          ) : (
            rows.map((row) => {
              const facts = lifecycleOf(row);
              const selected = row.id === peekId;
              return (
                <Box
                  key={row.id}
                  role="button"
                  tabIndex={0}
                  aria-label={`${row.name} — open summary`}
                  onClick={() => setPeek(row.id)}
                  onKeyDown={(event: React.KeyboardEvent) => {
                    // The row contains its own buttons (brand name, readiness
                    // chips, open link). Their Enter/Space must reach them, not
                    // be swallowed here, so only act on the row itself.
                    if (event.target !== event.currentTarget) return;
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      setPeek(row.id);
                    }
                  }}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', lg: GRID },
                    columnGap: '12px',
                    rowGap: '8px',
                    alignItems: 'center',
                    px: '16px',
                    py: '11px',
                    cursor: 'pointer',
                    borderBottom: `1px solid ${bvColor.borderSubtle}`,
                    bgcolor: selected ? bvColor.surfaceTint : 'transparent',
                    transition: bvTransition,
                    '&:hover': { bgcolor: selected ? bvColor.surfaceTint : bvColor.page },
                    '&:focus-visible': { outline: `2px solid ${bvColor.accent}`, outlineOffset: -2 },
                    '&:last-of-type': { borderBottom: 'none' },
                  }}
                >
                  {/* brand */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '11px', minWidth: 0 }}>
                    <LogoFrame name={row.name} url={row.logoUrl} size={40} artSize={32} monogramSize={14} />
                    <Box sx={{ minWidth: 0 }}>
                      <ButtonBase
                        onClick={(event) => {
                          // The name link opens the full page rather than the peek.
                          event.stopPropagation();
                          navigate(bvRoutes.brand(row.id));
                        }}
                        sx={{
                          ...bvType.tablePrimary,
                          color: bvColor.ink,
                          borderRadius: '4px',
                          '&:hover': { color: bvColor.accentHover, textDecoration: 'underline' },
                          '&:focus-visible': { outline: `2px solid ${bvColor.accent}`, outlineOffset: 2 },
                        }}
                      >
                        {row.name}
                      </ButtonBase>
                      <Box
                        sx={{
                          ...bvType.caption,
                          color: bvColor.muted,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {companyFactsLine(row)}
                      </Box>
                    </Box>
                  </Box>

                  {/* readiness */}
                  <Box sx={{ pr: '36px' }}>
                    <ReadinessChipRow
                      chips={readinessChipsOf(row)}
                      onActivate={(chip) => navigate(chipTarget(chip.key, row.id))}
                    />
                  </Box>

                  {/* stage */}
                  <Box>
                    <StagePill stage={facts.stage} />
                  </Box>

                  {/* products */}
                  <Box
                    sx={{
                      ...bvType.tableSecondary,
                      color: facts.hasProducts ? bvColor.ink3 : bvColor.muted,
                      ...tabular,
                    }}
                  >
                    <Box title={productsScopeTitle(row)}>
                      {productsLabel(row)}
                      {row.productCount > 0 && (
                        <Box component="span" sx={{ display: 'block', ...bvType.microCaption, color: bvColor.muted }}>
                          {productsScopeLabel(row)}
                        </Box>
                      )}
                    </Box>
                  </Box>

                  {/* dealer check · spec mapping */}
                  <Box sx={{ minWidth: 0 }}>
                    {(() => {
                      const dealer = dealerVerifierSummary(dealerLookup?.(row));
                      const specSource = row.specMapping?.source || 'generic';
                      return (
                        <>
                          <Box
                            title={dealerLookup ? dealer.detail : 'Loading dealer lists…'}
                            sx={{
                              ...bvType.tableSecondary,
                              color: dealer.ready ? bvColor.ink3 : bvColor.muted,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {dealerLookup ? dealer.label : '…'}
                          </Box>
                          <Box
                            title={SPEC_MAPPING_NOTE[specSource]}
                            sx={{
                              ...bvType.caption,
                              mt: '3px',
                              color: specSource === 'generic' ? bvColor.muted : bvColor.ink3,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            Specs: {SPEC_MAPPING_LABEL[specSource]}
                          </Box>
                        </>
                      );
                    })()}
                  </Box>

                  {/* claimed by · ingestion */}
                  <Box sx={{ minWidth: 0 }}>
                    <Box
                      sx={{
                        ...bvType.tableSecondary,
                        color: facts.claimed
                          ? bvColor.ink3
                          : facts.claimStatus === 'requested'
                            ? bvColor.warning
                            : bvColor.muted,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {facts.claimed
                        ? row.claimedBy.organizationName
                        : facts.claimStatus === 'requested'
                          ? `${row.claimedBy.organizationName || 'Organization'} · requested`
                          : 'unclaimed'}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', mt: '3px' }}>
                      <StatusDot color={ingestionDotColor(row)} />
                      <Box
                        sx={{
                          ...bvType.caption,
                          color: facts.configured ? (facts.failing ? bvColor.danger : bvColor.ink3) : bvColor.muted,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {row.ingestion.source}
                        {facts.configured && row.ingestion.paused ? ' · schedule off' : ''}
                      </Box>
                    </Box>
                  </Box>

                  {/* open affordance */}
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <ButtonBase
                      aria-label={`Open ${row.name} brand page`}
                      onClick={(event) => {
                        event.stopPropagation();
                        navigate(bvRoutes.brand(row.id));
                      }}
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: '6px',
                        color: bvColor.ink3,
                        border: `1px solid ${bvColor.border}`,
                        transition: bvTransition,
                        '&:hover': { color: bvColor.accent, bgcolor: bvColor.borderSubtle },
                        '&:focus-visible': { outline: `2px solid ${bvColor.accent}`, outlineOffset: 1 },
                      }}
                    >
                      <IconArrowOut size={16} />
                    </ButtonBase>
                  </Box>
                </Box>
              );
            })
          )}
        </Box>

        {!isLoading && unregisteredBrands.length > 0 && (
          <BvCard
            title="Not in the directory"
            caption="These names are on products, but there is no brand record, so they are missing from the list above. Add one to stub it in. You can fill in whatever is still missing on its page, or add the name as an alias on the brand it already belongs to."
          >
            {createMutation.isError && !createOpen && (
              <Alert severity="error" sx={{ mx: '13px', mt: '10px' }}>
                {(createMutation.error as Error).message}
              </Alert>
            )}
            <Box sx={{ maxHeight: 320, overflow: 'auto' }}>
              {unregisteredBrands.map((item, index) => (
                <Box
                  key={item.name}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    px: '13px',
                    py: '9px',
                    borderTop: index === 0 ? 'none' : `1px solid ${bvColor.borderSubtle}`,
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ ...bvType.tablePrimary, color: bvColor.ink }}>{item.name}</Box>
                    <Box sx={{ ...bvType.caption, color: bvColor.muted, ...tabular }}>
                      {item.productCount.toLocaleString()} {item.productCount === 1 ? 'product' : 'products'}
                    </Box>
                  </Box>
                  <BvButton
                    size="sm"
                    tone="primary"
                    aria-label={`Add ${item.name}`}
                    disabled={createMutation.isPending}
                    onClick={() => createMutation.mutate(item.name)}
                  >
                    {createMutation.isPending && createMutation.variables === item.name ? 'Adding…' : 'Add'}
                  </BvButton>
                </Box>
              ))}
            </Box>
          </BvCard>
        )}
      </Box>

      <BrandPeekDrawer rows={rows} brandId={peekId} onClose={() => setPeek(null)} onSelect={setPeek} />

      <Dialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        fullWidth
        maxWidth="xs"
        fullScreen={fullScreenDialog}
      >
        <DialogTitle sx={{ ...bvType.cardTitle, fontSize: 16 }}>Add brand</DialogTitle>
        <DialogContent>
          <Box sx={{ ...bvType.caption, color: bvColor.muted, mb: '8px' }}>
            A name is enough. The brand is added to the directory, and you fill in whatever is still missing on its page.
          </Box>
          <TextField
            autoFocus
            margin="dense"
            label="Brand name"
            fullWidth
            value={newName}
            onChange={(event) => setNewName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && newName.trim() && !createMutation.isPending) {
                event.preventDefault();
                createMutation.mutate(newName.trim());
              }
            }}
          />
          {createMutation.error && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {(createMutation.error as Error).message}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <BvButton onClick={() => setCreateOpen(false)}>Cancel</BvButton>
          <BvButton
            tone="primary"
            disabled={!newName.trim() || createMutation.isPending}
            onClick={() => createMutation.mutate(newName.trim())}
          >
            {createMutation.isPending ? 'Adding…' : 'Add brand'}
          </BvButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BrandVendorsDirectoryPage;
